import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Edit, Trash2, ExternalLink, Eye, EyeOff, Save, X, Image as ImageIcon, Key, Copy, Check } from "lucide-react";

const CATEGORIES = [
  "Product Spotlights",
  "Building Trends",
  "Contractor Tips",
  "Manufacturer News",
  "Market Insights",
];

const EMPTY = {
  title: "",
  slug: "",
  category: CATEGORIES[0],
  excerpt: "",
  body: "",
  featured_image_url: "",
  author: "",
  manufacturer_name: "",
  meta_description: "",
  published: true,
  related_product_ids: [],
};

export default function AdminInsights() {
  const [posts, setPosts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);   // null | "new" | post object
  const [filter, setFilter] = useState("all");    // all | published | drafts

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [p, pr] = await Promise.all([
        api.get("/insights"),
        api.get("/products").catch(() => ({ data: [] })),
      ]);
      setPosts(p.data);
      setProducts(pr.data);
    } catch (e) {
      console.error("Failed to load insights:", e);
      toast.error("Failed to load insights");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    if (filter === "published") return posts.filter((p) => p.published);
    if (filter === "drafts") return posts.filter((p) => !p.published);
    return posts;
  }, [posts, filter]);

  const togglePublish = async (post) => {
    try {
      await api.patch(`/insights/${post.id}`, { published: !post.published });
      toast.success(post.published ? "Unpublished" : "Published");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/insights/${post.id}`);
      toast.success("Deleted");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  if (editing) {
    return (
      <InsightEditor
        initial={editing === "new" ? EMPTY : editing}
        products={products}
        onSaved={() => { setEditing(null); load(); }}
        onCancel={() => setEditing(null)}
      />
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-14" data-testid="admin-insights-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Admin · Content</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black uppercase mt-1 leading-none text-[#1A365D]">Building Insights.</h1>
          <p className="mt-2 text-slate-600">Publish manufacturer product blogs and market commentary. Drafts stay hidden from the public site until you publish.</p>
        </div>
        <button onClick={() => setEditing("new")} className="btn-primary flex items-center gap-2" data-testid="new-insight-btn">
          <Plus size={16}/> New Insight
        </button>
      </div>

      <AppPasswordsCard/>

      <div className="mt-8 flex items-center gap-2 flex-wrap">
        {["all", "published", "drafts"].map((k) => (
          <button
            key={k}
            onClick={() => setFilter(k)}
            data-testid={`admin-insight-filter-${k}`}
            className={`px-4 py-2 text-xs font-mono-ll tracking-widest uppercase border-2 ${
              filter === k ? "bg-[#1A365D] text-white border-[#1A365D]" : "bg-white text-[#1A365D] border-[#1A365D]"
            }`}
          >
            {k} ({k === "all" ? posts.length : k === "published" ? posts.filter((p) => p.published).length : posts.filter((p) => !p.published).length})
          </button>
        ))}
      </div>

      <div className="mt-6 brutal-card-flat overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-[#1A365D] text-white uppercase font-mono-ll text-[11px] tracking-widest">
            <tr>
              <th className="px-4 py-3 text-left">Title</th>
              <th className="px-4 py-3 text-left">Category</th>
              <th className="px-4 py-3 text-left">Status</th>
              <th className="px-4 py-3 text-left">Updated</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">LOADING…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={5} className="px-4 py-12 text-center font-mono-ll tracking-widest text-slate-500">NO INSIGHTS YET — CLICK "NEW INSIGHT" TO CREATE ONE</td></tr>
            ) : filtered.map((post) => (
              <tr key={post.id} className="border-t-2 border-slate-200 hover:bg-slate-50" data-testid={`admin-insight-row-${post.id}`}>
                <td className="px-4 py-3">
                  <div className="font-semibold text-[#1A365D]">{post.title}</div>
                  <div className="text-xs text-slate-500 font-mono-ll">/insights/{post.slug}</div>
                </td>
                <td className="px-4 py-3 text-xs">{post.category}</td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-1 text-[10px] font-mono-ll tracking-widest uppercase ${post.published ? "bg-green-700 text-white" : "bg-yellow-500 text-white"}`}>
                    {post.published ? "Published" : "Draft"}
                  </span>
                </td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(post.updated_at).toLocaleString()}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1">
                    <button onClick={() => setEditing(post)} className="p-2 text-[#1A365D] hover:bg-slate-100" title="Edit" data-testid={`edit-insight-${post.id}`}>
                      <Edit size={16}/>
                    </button>
                    <button onClick={() => togglePublish(post)} className="p-2 text-[#1A365D] hover:bg-slate-100" title={post.published ? "Unpublish" : "Publish"} data-testid={`toggle-publish-${post.id}`}>
                      {post.published ? <EyeOff size={16}/> : <Eye size={16}/>}
                    </button>
                    {post.published && (
                      <a href={`/insights/${post.slug}`} target="_blank" rel="noopener noreferrer" className="p-2 text-[#1A365D] hover:bg-slate-100" title="View live" data-testid={`view-insight-${post.id}`}>
                        <ExternalLink size={16}/>
                      </a>
                    )}
                    <button onClick={() => remove(post)} className="p-2 text-[#C62828] hover:bg-red-50" title="Delete" data-testid={`delete-insight-${post.id}`}>
                      <Trash2 size={16}/>
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InsightEditor({ initial, products, onSaved, onCancel }) {
  const [form, setForm] = useState({ ...EMPTY, ...initial, related_product_ids: initial.related_product_ids || [] });
  const [saving, setSaving] = useState(false);
  const isEdit = !!initial.id;
  const upd = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      related_product_ids: f.related_product_ids.includes(id)
        ? f.related_product_ids.filter((x) => x !== id)
        : [...f.related_product_ids, id],
    }));
  };

  const save = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.body.trim()) {
      toast.error("Title and body are required.");
      return;
    }
    setSaving(true);
    try {
      if (isEdit) {
        await api.patch(`/insights/${initial.id}`, form);
        toast.success("Saved");
      } else {
        await api.post("/insights", form);
        toast.success("Created");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={save} className="max-w-5xl mx-auto px-6 lg:px-10 py-14" data-testid="insight-editor">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="label-tag">Admin · {isEdit ? "Edit" : "New"} Insight</div>
          <h1 className="font-heading text-3xl md:text-5xl font-black uppercase mt-1 leading-none text-[#1A365D]">
            {isEdit ? "Edit post." : "Create post."}
          </h1>
        </div>
        <div className="flex gap-2">
          <button type="button" onClick={onCancel} className="btn-ghost flex items-center gap-2" data-testid="editor-cancel-btn">
            <X size={16}/> Cancel
          </button>
          <button type="submit" disabled={saving} className="btn-primary flex items-center gap-2" data-testid="editor-save-btn">
            <Save size={16}/> {saving ? "Saving…" : (isEdit ? "Save Changes" : "Create Insight")}
          </button>
        </div>
      </div>

      <div className="mt-8 grid lg:grid-cols-12 gap-6">
        {/* Main column */}
        <div className="lg:col-span-8 space-y-5">
          <Field label="Title *">
            <input className="ll-input" value={form.title} onChange={(e) => upd("title", e.target.value)} data-testid="editor-title-input"/>
          </Field>
          <Field label="URL Slug" hint={`/insights/${form.slug || "auto-generated-from-title"}`}>
            <input className="ll-input font-mono-ll" value={form.slug} onChange={(e) => upd("slug", e.target.value)} placeholder="leave blank to auto-generate" data-testid="editor-slug-input"/>
          </Field>
          <Field label="Excerpt" hint="Shown on the index card and in social shares (~160 chars).">
            <textarea className="ll-textarea" rows={2} value={form.excerpt} onChange={(e) => upd("excerpt", e.target.value)} data-testid="editor-excerpt-input"/>
          </Field>
          <Field label="Body (Markdown) *" hint="Paste from Marblism — supports markdown, GFM tables, and inline HTML (sanitized).">
            <textarea className="ll-textarea font-mono-ll text-sm" rows={20} value={form.body} onChange={(e) => upd("body", e.target.value)} data-testid="editor-body-input"/>
          </Field>
        </div>

        {/* Sidebar */}
        <div className="lg:col-span-4 space-y-5">
          <div className="brutal-card p-5 space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D]">Publish</h3>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.published} onChange={(e) => upd("published", e.target.checked)} data-testid="editor-published-toggle"/>
              <span className="text-sm font-bold">{form.published ? "Published (live on site)" : "Draft (hidden)"}</span>
            </label>
            <Field label="Category *">
              <select className="ll-select" value={form.category} onChange={(e) => upd("category", e.target.value)} data-testid="editor-category-select">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
          </div>

          <div className="brutal-card p-5 space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D] flex items-center gap-2">
              <ImageIcon size={18}/> Featured Image
            </h3>
            <Field label="Image URL" hint="Paste an image URL from Marblism / your CDN. Recommended: 1600×900px.">
              <input className="ll-input" value={form.featured_image_url} onChange={(e) => upd("featured_image_url", e.target.value)} placeholder="https://..." data-testid="editor-image-input"/>
            </Field>
            {form.featured_image_url && (
              <div className="aspect-[16/9] border-2 border-[#1A365D] overflow-hidden">
                <img src={form.featured_image_url} alt="preview" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }}/>
              </div>
            )}
          </div>

          <div className="brutal-card p-5 space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D]">Attribution</h3>
            <Field label="Author">
              <input className="ll-input" value={form.author} onChange={(e) => upd("author", e.target.value)} placeholder="e.g. LumberLoop USA Editorial" data-testid="editor-author-input"/>
            </Field>
            <Field label="Manufacturer / Brand">
              <input className="ll-input" value={form.manufacturer_name} onChange={(e) => upd("manufacturer_name", e.target.value)} placeholder="e.g. Huber ZIP System" data-testid="editor-mfr-input"/>
            </Field>
          </div>

          <div className="brutal-card p-5 space-y-4">
            <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D]">SEO</h3>
            <Field label="Meta Description" hint="What Google shows under the title (~155 chars).">
              <textarea className="ll-textarea" rows={3} value={form.meta_description} onChange={(e) => upd("meta_description", e.target.value)} data-testid="editor-meta-input"/>
            </Field>
          </div>

          {products.length > 0 && (
            <div className="brutal-card p-5 space-y-3">
              <h3 className="font-heading text-lg font-bold uppercase text-[#1A365D]">Related Products</h3>
              <p className="text-xs text-slate-600">Tag products mentioned in the article — they'll appear at the bottom of the post.</p>
              <div className="max-h-60 overflow-y-auto space-y-1 border-2 border-slate-200 p-2">
                {products.map((p) => (
                  <label key={p.id} className="flex items-start gap-2 text-sm hover:bg-slate-50 p-1 cursor-pointer">
                    <input type="checkbox" className="mt-1" checked={form.related_product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} data-testid={`editor-product-${p.id}`}/>
                    <span>
                      <span className="font-semibold text-[#1A365D]">{p.name}</span>
                      <span className="block text-[10px] font-mono-ll tracking-widest text-slate-500 uppercase">{p.manufacturer_name} · {p.category}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </form>
  );
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="label-tag block mb-2">{label}</label>
      {children}
      {hint && <div className="text-[10px] font-mono-ll tracking-widest text-slate-500 uppercase mt-1">{hint}</div>}
    </div>
  );
}

// ---------- Application Passwords (for Marblism / WordPress REST API) ----------
function AppPasswordsCard() {
  const [open, setOpen] = useState(false);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [newName, setNewName] = useState("Marblism");
  const [created, setCreated] = useState(null); // {username, password, name}
  const [copied, setCopied] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/app-passwords");
      setList(data);
    } catch (e) {
      console.error("Failed to load app passwords:", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (open) load(); }, [open, load]);

  const create = async () => {
    try {
      const { data } = await api.post("/app-passwords", { name: newName || "Untitled" });
      setCreated(data);
      setNewName("Marblism");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const revoke = async (id) => {
    if (!window.confirm("Revoke this application password? Any tools using it will stop working.")) return;
    try {
      await api.delete(`/app-passwords/${id}`);
      toast.success("Revoked");
      load();
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch {
      toast.error("Copy failed — select & copy manually.");
    }
  };

  const siteUrl = `${window.location.origin}/api`;

  return (
    <div className="mt-6 brutal-card-flat" data-testid="app-passwords-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full px-5 py-4 flex items-center justify-between border-b-2 border-slate-200 hover:bg-slate-50"
        data-testid="toggle-app-passwords"
      >
        <span className="flex items-center gap-2">
          <Key size={18} className="text-[#1A365D]"/>
          <span className="font-heading text-lg font-bold uppercase text-[#1A365D]">WordPress Application Passwords</span>
          <span className="px-2 py-0.5 bg-[#1A365D] text-white text-[10px] font-mono-ll tracking-widest uppercase">For Marblism</span>
        </span>
        <span className="font-mono-ll text-xs tracking-widest uppercase text-slate-500">
          {open ? "Hide" : "Show"}
        </span>
      </button>

      {open && (
        <div className="p-5 space-y-5">
          <div className="grid md:grid-cols-3 gap-3">
            <CredField label="Site URL" value={siteUrl} testid="cred-site-url" onCopy={() => copy(siteUrl, "site")} copied={copied === "site"}/>
            <CredField label="Username" value="info@lumberloopusa.com" testid="cred-username" onCopy={() => copy("info@lumberloopusa.com", "user")} copied={copied === "user"}/>
            <div className="brutal-card-flat p-3">
              <div className="label-tag mb-1">Application Password</div>
              <div className="text-xs text-slate-600 font-semibold">Generate one below ↓ (only shown once)</div>
            </div>
          </div>

          {created && (
            <div className="brutal-card p-5 bg-yellow-50 border-yellow-500" data-testid="new-app-password-banner">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <div className="font-heading text-lg font-bold uppercase text-[#1A365D]">New password — save it now!</div>
                  <div className="text-xs text-slate-600 mt-1">This password will <strong>NOT</strong> be shown again. Copy it before closing this banner.</div>
                </div>
                <button onClick={() => setCreated(null)} className="text-slate-500 hover:text-[#C62828]" data-testid="dismiss-new-password">
                  <X size={18}/>
                </button>
              </div>
              <div className="mt-4 bg-white border-2 border-[#1A365D] p-3 flex items-center justify-between gap-3 flex-wrap">
                <code className="font-mono-ll text-base text-[#1A365D] tracking-wider break-all" data-testid="new-password-value">{created.password}</code>
                <button onClick={() => copy(created.password, "newpw")} className="btn-ghost flex items-center gap-2 text-sm" data-testid="copy-new-password">
                  {copied === "newpw" ? <><Check size={14}/> Copied</> : <><Copy size={14}/> Copy</>}
                </button>
              </div>
              <div className="mt-3 text-xs text-slate-700">
                Paste this into Marblism's "Application Password" field. Username is <code className="bg-slate-100 px-1">{created.username}</code>.
              </div>
            </div>
          )}

          <div className="flex items-end gap-2 flex-wrap">
            <div className="flex-1 min-w-[200px]">
              <label className="label-tag block mb-1">New Password Label</label>
              <input
                className="ll-input"
                placeholder="e.g. Marblism"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                data-testid="new-app-password-name"
              />
            </div>
            <button onClick={create} className="btn-primary flex items-center gap-2" data-testid="generate-app-password">
              <Plus size={16}/> Generate Password
            </button>
          </div>

          {loading ? (
            <div className="text-xs font-mono-ll tracking-widest text-slate-500">LOADING…</div>
          ) : list.length === 0 ? (
            <div className="text-xs font-mono-ll tracking-widest text-slate-500 text-center py-4">NO APP PASSWORDS YET</div>
          ) : (
            <div className="border-2 border-slate-200">
              <table className="w-full text-sm">
                <thead className="bg-slate-100 uppercase font-mono-ll text-[10px] tracking-widest">
                  <tr>
                    <th className="px-3 py-2 text-left">Name</th>
                    <th className="px-3 py-2 text-left">Created</th>
                    <th className="px-3 py-2 text-left">Last Used</th>
                    <th className="px-3 py-2 text-right">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((ap) => (
                    <tr key={ap.id} className="border-t border-slate-200" data-testid={`app-pw-row-${ap.id}`}>
                      <td className="px-3 py-2 font-semibold text-[#1A365D]">{ap.name}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{new Date(ap.created_at).toLocaleDateString()}</td>
                      <td className="px-3 py-2 text-xs text-slate-500">{ap.last_used_at ? new Date(ap.last_used_at).toLocaleString() : "Never"}</td>
                      <td className="px-3 py-2 text-right">
                        <button onClick={() => revoke(ap.id)} className="text-[#C62828] hover:underline text-xs font-mono-ll tracking-widest uppercase" data-testid={`revoke-app-pw-${ap.id}`}>
                          Revoke
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <details className="text-sm text-slate-700">
            <summary className="cursor-pointer font-mono-ll text-xs tracking-widest uppercase text-[#1A365D]">How to connect Marblism →</summary>
            <ol className="mt-3 space-y-2 list-decimal pl-5">
              <li>In Marblism, choose <strong>WordPress</strong> as the publishing destination.</li>
              <li>Paste <code className="bg-slate-100 px-1">{siteUrl}</code> as the <strong>Site URL</strong>. (The <code>/api</code> suffix is required — Marblism will append <code>/wp-json/...</code> automatically.)</li>
              <li>Paste <code className="bg-slate-100 px-1">info@lumberloopusa.com</code> as the <strong>Username</strong>.</li>
              <li>Click <strong>Generate Password</strong> above and paste the generated value into Marblism's <strong>Application Password</strong> field.</li>
              <li>Save & test. Marblism's posts will appear at <code className="bg-slate-100 px-1">/insights</code> the moment it publishes.</li>
            </ol>
          </details>
        </div>
      )}
    </div>
  );
}

function CredField({ label, value, testid, onCopy, copied }) {
  return (
    <div className="brutal-card-flat p-3">
      <div className="label-tag mb-1">{label}</div>
      <div className="flex items-center justify-between gap-2">
        <code className="text-xs text-[#1A365D] font-semibold break-all" data-testid={testid}>{value}</code>
        <button onClick={onCopy} className="text-slate-500 hover:text-[#C62828] flex-shrink-0">
          {copied ? <Check size={14}/> : <Copy size={14}/>}
        </button>
      </div>
    </div>
  );
}
